import { queryClient } from "../index";

const LOGOUT_CHANNEL_NAME = "toca_health_logout";

function clearAllStorage() {
  localStorage.clear();
  sessionStorage.clear();
}

/**
 * Like performLogout(), but explicitly resets the preference keys
 * (fallback_org, fallback_location) before logging out. Since 70f9b50,
 * performLogout()'s clearAllStorage() wipes everything anyway, so the
 * explicit removeItems are functionally redundant. The named alias is
 * retained for initSession.js's schema-migration call site, where
 * signaling "reset preferences too" is meaningful at the point of use.
 */
export function performLogoutAndResetPreferences() {
  localStorage.removeItem("fallback_org");
  localStorage.removeItem("fallback_location");
  performLogout();
}

/**
 * Centralized logout utility.
 * - Signals other tabs via BroadcastChannel + storage event
 * - Clears localStorage, sessionStorage, React Query cache
 * - Redirects to /login
 */
export function performLogout() {
  // Signal other tabs BEFORE clearing storage
  try {
    const channel = new BroadcastChannel(LOGOUT_CHANNEL_NAME);
    channel.postMessage({ type: "logout" });
    channel.close();
  } catch {
    // BroadcastChannel not supported — storage event fallback is enough
  }

  // Set sessionExpired flag so the storage event listener in other tabs picks it up.
  // Must happen BEFORE localStorage.clear() so the event fires with this key.
  // Use a UUID so two near-simultaneous logouts don't collide on the same value
  // (the storage event won't fire if the value is unchanged).
  localStorage.setItem("sessionExpired", crypto.randomUUID());

  clearAllStorage();

  try {
    queryClient.clear();
  } catch {
    // queryClient may not be available yet
  }

  window.location.href = "/login";
}

/**
 * Listens for logout signals from other tabs.
 * Call once at app startup (e.g. in SessionExpirationHandler).
 * Returns a cleanup function.
 */
export function listenForCrossTabLogout() {
  const handleStorageChange = (event) => {
    if (event.key === "sessionExpired") {
      clearAllStorage();
      try {
        queryClient.clear();
      } catch {
        // ignore
      }
      window.location.href = "/login";
    }
  };

  window.addEventListener("storage", handleStorageChange);

  let channel;
  try {
    channel = new BroadcastChannel(LOGOUT_CHANNEL_NAME);
    channel.onmessage = (event) => {
      if (event.data?.type === "logout") {
        clearAllStorage();
        try {
          queryClient.clear();
        } catch {
          // ignore
        }
        window.location.href = "/login";
      }
    };
  } catch {
    // BroadcastChannel not supported — storage event is the fallback
  }

  return () => {
    window.removeEventListener("storage", handleStorageChange);
    if (channel) {
      channel.close();
    }
  };
}
